import { useMemo } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import { formatDisplayShort } from '../../../lib/dateUtils';

/**
 * Group outstanding-repair defect rows by building/room for display.
 * Mirrors the previous inline grouping in Sites.jsx (numeric-aware sort).
 */
function groupRepairsByLocation(defects) {
  const map = new Map();
  defects.forEach((r) => {
    const building = (r.building || '').trim() || 'Unspecified Building';
    const room = (r.room || '').trim();
    const key = `${building}|||${room || 'Unspecified Room'}`;
    if (!map.has(key)) {
      map.set(key, { building, room: room || 'Unspecified Room', items: [] });
    }
    map.get(key).items.push(r);
  });
  return Array.from(map.values()).sort((a, b) => {
    const aKey = `${a.building} ${a.room}`;
    const bKey = `${b.building} ${b.room}`;
    return aKey.localeCompare(bKey, undefined, { numeric: true });
  });
}

/**
 * Read-only modal listing a site's outstanding repairs, grouped by location,
 * with an action to start a repair session. Data fetching stays in the parent;
 * this component only presents the supplied payload.
 */
export default function SiteRepairsModal({
  isOpen,
  onClose,
  site,
  payload,
  isLoading,
  isError,
  onStartRepair,
}) {
  const totalCount = payload?.pagination?.totalCount || 0;
  const grouped = useMemo(() => groupRepairsByLocation(payload?.defects || []), [payload]);
  const siteLabel = site?.display_name ?? site?.name ?? site?.site_code ?? 'Site';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Outstanding Repairs" className="repairs-modal">
      <p className="text-muted" style={{ marginBottom: 12 }}>{siteLabel}</p>
      <p className="text-muted repairs-modal-count">{totalCount} outstanding asset(s)</p>
      {isLoading ? (
        <p className="text-muted">Loading outstanding repairs...</p>
      ) : isError ? (
        <p className="page-error">Failed to load outstanding repairs.</p>
      ) : totalCount === 0 ? (
        <p className="text-muted">No outstanding repairs for this site.</p>
      ) : (
        <div className="table-container repairs-modal-groups">
          {grouped.map((group) => (
            <div key={`${group.building}-${group.room}`} className="repairs-modal-group">
              <h4 className="repairs-modal-group-title">{group.building} - {group.room}</h4>
              <ul className="reports-session-list repairs-modal-list">
                {group.items.map((item) => (
                  <li key={item.asset_id} className="reports-session-card repairs-modal-item">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <strong>{item.asset_name || item.equipment_code || item.asset_code || `Asset ${item.asset_id}`}</strong>
                      <Badge variant="danger">Outstanding</Badge>
                    </div>
                    <div className="text-muted" style={{ marginTop: 4 }}>
                      {item.asset_type} - Failed {item.failed_at ? formatDisplayShort(item.failed_at) : '-'}
                    </div>
                    {item.defects_found ? (
                      <div style={{ marginTop: 4 }}>{item.defects_found}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className="form-actions">
        <Button type="button" onClick={onClose}>
          Close
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={() => onStartRepair(site?.id)}
          disabled={!site?.id || totalCount === 0}
        >
          Start Repair Session
        </Button>
      </div>
    </Modal>
  );
}
